import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { cancel, getPrompt, startSignIn, subscribe } from "../../access/authPrompt";
import { promptFor } from "../../access/registry";

/**
 * The sign-in modal: the view of `access/authPrompt`, mounted once in App.js.
 *
 * An in-place dialog over whatever the user was doing (FRS FR19). Not a route —
 * the URL does not change — so Cancel, Escape, the backdrop and the close
 * control all leave the page exactly as it was (FR21), and Radix hands focus
 * back to the control that opened it.
 *
 * Popup problems keep it open with something to say (FR22): only Cancel
 * dismisses it. Above the cookie banner (`z-[100]`), which would otherwise
 * cover the button on a first visit.
 */
const NEXT_STEP = {
  click: "We'll finish what you started as soon as you're signed in.",
  direct_url: "This page needs a free account. Sign in and it opens.",
  http_401: "This needs a free account. Sign in and we'll carry on.",
};

const ERRORS = {
  popup_blocked:
    "Your browser blocked the sign-in window. Allow pop-ups for zatoplan.com, then try again.",
  popup_closed: "Sign-in was closed.",
  network: "We couldn't reach Google. Check your connection and try again.",
  in_app_browser:
    "Google sign-in doesn't work in this app's built-in browser, so we've opened the page in your browser. Sign in there and press the button again.",
  error: "Sign-in didn't work. Please try again.",
};

const SignInModal = () => {
  const [prompt, setPrompt] = useState(getPrompt);
  useEffect(() => {
    const unsubscribe = subscribe(setPrompt);
    // A prompt opened before this effect ran (a gate's own mount effect runs
    // first) was announced to nobody; pick it up now.
    setPrompt(getPrompt());
    return unsubscribe;
  }, []);

  const open = Boolean(prompt);
  const pending = prompt?.status === "pending";
  const error = prompt?.error ? ERRORS[prompt.error] || ERRORS.error : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) cancel("cancelled");
      }}
    >
      {open && (
        <DialogContent className="z-[110] max-w-sm" overlayClassName="z-[110]">
          <DialogHeader>
            <DialogTitle className="font-display -tracking-[0.02em]">
              Sign in to {promptFor(prompt.featureId)}
            </DialogTitle>
            <DialogDescription>
              {NEXT_STEP[prompt.trigger] || NEXT_STEP.click}
            </DialogDescription>
          </DialogHeader>

          {error && (
            <p
              role="alert"
              className="rounded-md border px-3 py-2 text-[12.5px] text-foreground"
            >
              {error}
            </p>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              className="font-mono text-xs font-light tracking-wide uppercase rounded-md"
              variant="outline"
              size="sm"
              onClick={() => cancel("cancelled")}
            >
              Cancel
            </Button>
            <Button
              className="bg-zp-ink text-white font-mono text-xs font-light px-6 tracking-wide rounded-md uppercase hover:bg-zp-link"
              size="sm"
              onClick={startSignIn}
              disabled={pending}
            >
              {pending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  Waiting for Google…
                </>
              ) : error ? (
                "Try again"
              ) : (
                "Continue with Google"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default SignInModal;
